import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type AppRole = "admin" | "student" | "school" | "coaching_center" | "institution";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: { full_name: string | null; avatar_url: string | null } | null;
  isAdmin: boolean;
  role: AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  isAdmin: false,
  role: null,
  loading: true,
  signOut: async () => {},
});

export const ROLE_HOME: Record<AppRole, string> = {
  admin: "/admin",
  student: "/dashboard",
  school: "/institution-dashboard",
  coaching_center: "/institution-dashboard",
  institution: "/institution-dashboard",
};

export const INSTITUTION_ROLES: AppRole[] = ["school", "coaching_center", "institution"];
export const ROLE_PRIORITY: AppRole[] = ["admin", "school", "coaching_center", "institution", "student"];
export const getPrimaryRole = (roles: readonly string[]): AppRole | null =>
  ROLE_PRIORITY.find((candidate) => roles.includes(candidate)) ?? null;

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthContextType["profile"]>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let currentUserId: string | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const nextUserId = session?.user?.id ?? null;
        setSession(session);
        setUser(session?.user ?? null);

        if (event === "SIGNED_OUT" || !session?.user) {
          currentUserId = null;
          setProfile(null);
          setIsAdmin(false);
          setRole(null);
          setLoading(false);
          return;
        }

        // Token refresh for the same user — no need to refetch role/profile
        // or flip loading back to true (would trigger guard spinners).
        if (event === "TOKEN_REFRESHED" && nextUserId === currentUserId) {
          return;
        }

        currentUserId = nextUserId;
        // Gate guards until profile + role are resolved.
        setLoading(true);

        // Defer fetching to avoid deadlocks inside the auth callback.
        setTimeout(async () => {
          try {
            const [{ data: profileData }, { data: rolesData }] = await Promise.all([
              supabase.from("profiles").select("full_name, avatar_url").eq("user_id", session.user.id).maybeSingle(),
              supabase.from("user_roles").select("role").eq("user_id", session.user.id),
            ]);
            // Ignore if user changed mid-flight
            if (currentUserId !== session.user.id) return;
            setProfile(profileData ?? null);
            const roles = (rolesData?.map((r) => r.role) ?? []) as string[];
            const primary = getPrimaryRole(roles);
            setRole(primary);
            setIsAdmin(primary === "admin");
          } finally {
            if (currentUserId === session.user.id) setLoading(false);
          }
        }, 0);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      // Hard reset to the landing page so no protected route can briefly
      // evaluate against the cleared session and flash an unauthorized toast.
      window.location.replace("/");
    }
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, isAdmin, role, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

